import React from 'react';
import { Box, Paper } from '@mui/material';
import BasicInfoSection from './sections/BasicInfoSection';
import ClassificationSection from './sections/ClassificationSection';
import DescriptionSection from './sections/DescriptionSection';
import RepRangeSection from './sections/RepRangeSection';
import MediaSection from './sections/MediaSection';
import VariantInfoAlert from './VariantInfoAlert';

/**
 * ExerciseForm - Unified form component for creating/editing exercises
 * Used by both ExerciseDialog and ExercisePage
 */
const ExerciseForm = ({
    // Form hook values
    formValues,
    setFieldValue,
    setFieldTouched,

    // Upload states
    uploadStates,
    isUploading,

    // Previews
    imagePreview,
    coverPreview,

    // Video state
    isVideoUploadMode,
    setIsVideoUploadMode,
    videoFile,
    videoFileName,
    videoPreviewUrl,
    existingVideoUrl,

    // Handlers
    handleImageUpload,
    handleCoverExtracted,
    handleVideoFileChange,
    handleVideoUrlChange,
    handleRemoveVideo,

    // Validation. The English-name uniqueness props (nameExistsError / isCheckingName /
    // checkNameExists) are no longer destructured: the English name is a translation row now, so
    // nothing in this tree renders or validates it. Call sites may still pass them harmlessly.
    errors,
    touched,
    nameHebExistsError,
    isCheckingNameHeb,
    checkNameHebExists,

    // Exercise data for variant/permissions info
    exercise,
    isSuperAdmin = false,

    // Mode: 'dialog' or 'page'
    mode = 'dialog'
}) => {
    // Handle YouTube thumbnail extraction
    const handleYouTubeThumbnailExtracted = async (thumbnailUrl) => {
        if (!thumbnailUrl) return;

        try {
            const response = await fetch(thumbnailUrl);
            const blob = await response.blob();
            const file = new File([blob], 'youtube-thumbnail.jpg', { type: 'image/jpeg' });
            const previewUrl = URL.createObjectURL(file);

            // Trigger cover extraction handler with the thumbnail
            handleCoverExtracted(blob, previewUrl, 0);
        } catch (error) {
            console.error('Error fetching YouTube thumbnail:', error);
        }
    };

    const sectionSpacing = mode === 'dialog' ? 3 : 4;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: sectionSpacing }}>
            {/* Variant/System Resource Info Alert */}
            <VariantInfoAlert
                exercise={exercise}
                isSuperAdmin={isSuperAdmin}
                isEditing={true}
            />

            {/* Basic Information - Names */}
            <Paper
                elevation={0}
                sx={{
                    p: mode === 'dialog' ? 2 : 3,
                    backgroundColor: '#FCFCFD',
                    border: '1px solid #E2E8F0',
                    borderRadius: '12px'
                }}
            >
                {/* Only the CANONICAL (Hebrew) name is edited here; every other language goes
                    through the schema-driven translation panel the section hosts. The English
                    name/uniqueness props are therefore no longer passed. */}
                <BasicInfoSection
                    formValues={formValues}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    errors={errors}
                    touched={touched}
                    nameHebExistsError={nameHebExistsError}
                    isCheckingNameHeb={isCheckingNameHeb}
                    checkNameHebExists={checkNameHebExists}
                    mode={mode}
                />
            </Paper>

            {/* Classification - Muscle & Resistance */}
            <Paper
                elevation={0}
                sx={{
                    p: mode === 'dialog' ? 2 : 3,
                    backgroundColor: '#FCFCFD',
                    border: '1px solid #E2E8F0',
                    borderRadius: '12px'
                }}
            >
                <ClassificationSection
                    formValues={formValues}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    errors={errors}
                    touched={touched}
                    exercise={exercise}
                    isSuperAdmin={isSuperAdmin}
                />
            </Paper>

            {/* Rep Range Defaults - hidden for catalyst exercises */}
            {!formValues.catalyst && (
                <Paper
                    elevation={0}
                    sx={{
                        p: mode === 'dialog' ? 2 : 3,
                        backgroundColor: '#FCFCFD',
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px'
                    }}
                >
                    <RepRangeSection
                        formValues={formValues}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        errors={errors}
                        touched={touched}
                    />
                </Paper>
            )}

            {/* Description */}
            <Paper
                elevation={0}
                sx={{
                    p: mode === 'dialog' ? 2 : 3,
                    backgroundColor: '#FCFCFD',
                    border: '1px solid #E2E8F0',
                    borderRadius: '12px'
                }}
            >
                <DescriptionSection
                    formValues={formValues}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    errors={errors}
                    touched={touched}
                />
            </Paper>

            {/* Media - Images & Video */}
            <Paper
                elevation={0}
                sx={{
                    p: mode === 'dialog' ? 2 : 3,
                    backgroundColor: '#FCFCFD',
                    border: '1px solid #E2E8F0',
                    borderRadius: '12px'
                }}
            >
                <MediaSection
                    imagePreview={imagePreview}
                    coverPreview={coverPreview}
                    uploadStates={uploadStates}
                    handleImageUpload={handleImageUpload}
                    isVideoUploadMode={isVideoUploadMode}
                    setIsVideoUploadMode={setIsVideoUploadMode}
                    videoFile={videoFile}
                    videoFileName={videoFileName}
                    videoPreviewUrl={videoPreviewUrl}
                    existingVideoUrl={existingVideoUrl}
                    handleVideoFileChange={handleVideoFileChange}
                    handleVideoUrlChange={handleVideoUrlChange}
                    handleRemoveVideo={handleRemoveVideo}
                    handleCoverExtracted={handleCoverExtracted}
                    formValues={formValues}
                    setFieldValue={setFieldValue}
                    onYouTubeThumbnailExtracted={handleYouTubeThumbnailExtracted}
                />
            </Paper>
        </Box>
    );
};

export default ExerciseForm;
